/**
 * 自動ログアウトの見張り（#189、docs/76 §2.3・§2.4）。App に1つだけ置く（app.tsx）。判定はサーバー、ここは案内だけ。
 *
 *   ・期限＝毎回の答えの x-gb-idle-left（lib/autoLogout.ts の deadline）。期限の5分前に passive の /me で確かめ、
 *     まだ5分以内なら上の帯「まもなく自動ログアウトします（残り 4:59）」＋「ログイン継続」（X-User-Action つきの /me）。
 *     帯を出すときに見積書の書きかけを送る（④）。人が操作したら POST /auth/alive（X-User-Action）で延ばして帯を消す
 *   ・画面の操作だけ（API を呼ばない入力・スクロール）でも、前に延ばしてから1分たっていれば /auth/alive を1本
 *   ・タブに戻ったとき（visibilitychange）・PC のスリープから戻ったとき（30 秒ごとの時計のずれ）は passive の /me で確かめる（A-d）
 *   ・切れた（lib/autoLogout.ts の sessionLost）：idle＝止めの画面 g を画面全体に重ねる（下の画面は操作できない）。
 *     expired（ほかの理由の 401）＝控えを捨ててログインの画面へ（/login?next=<今の画面>。ページごと移る＝新しい版も読む）
 *   ・ログインした人が変わったら、ほかの人の退避した入力を消す（④）
 */
import { ALIVE_EVERY_MS, AUTO_LOGOUT_MSG, AUTO_LOGOUT_WARN_SEC, countdownText, loginPathWithNext, type Me } from "@genbaryoku/contract";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { api } from "../api.js";
import { setMe, useMe } from "../auth.js";
import { PASSIVE, USER_ACTION, flushUnsaved, lastActionSent, onUserInput, pruneStashes, setLoggedIn, snapshot, subscribe } from "../lib/autoLogout.js";
import { releaseLeaveGuards } from "./BackButton.jsx";
import { IdleStop } from "./StopScreen.jsx";

/** passive の確かめ（帯の予定・タブ復帰・スリープの戻り）。答えのヘッダーが期限を入れ直す。切れていれば api.ts が sessionLost を呼ぶ */
async function checkPassive(): Promise<void> {
  try {
    await api<Me>("GET", "/me", undefined, PASSIVE);
  } catch {
    // 切れた（401）は api.ts が扱う。つながらないときは次の機会に
  }
}

/** 人の操作の延長（X-User-Action つき・204） */
let aliveInflight = false;
async function sendAlive(): Promise<void> {
  if (aliveInflight) return;
  aliveInflight = true;
  try {
    await api<void>("POST", "/auth/alive", undefined, USER_ACTION);
  } catch {
    // 同上
  } finally {
    aliveInflight = false;
  }
}

export function AutoLogoutHost() {
  const qc = useQueryClient();
  const me = useMe();
  const st = useSyncExternalStore(subscribe, snapshot);
  const loggedIn = !!me.data;
  const accountId = me.data?.session.accountId ?? null;
  const [warn, setWarn] = useState(false);
  const [, setTick] = useState(0);
  const warnRef = useRef(false);
  warnRef.current = warn;

  // ログインしているか（401 を「切れた」と見るのはログインしていたときだけ）・ほかの人の退避した入力を消す
  useEffect(() => {
    setLoggedIn(loggedIn);
    if (accountId) pruneStashes(accountId);
  }, [loggedIn, accountId]);

  // 期限の5分前：passive で確かめてから帯（ほかのタブで操作していれば延びている）
  useEffect(() => {
    if (!loggedIn || st.ended || st.deadline === null) {
      setWarn(false);
      return;
    }
    const deadline = st.deadline;
    const warnAt = deadline - AUTO_LOGOUT_WARN_SEC * 1000;
    if (Date.now() < warnAt) {
      setWarn(false);
      const t = window.setTimeout(() => void checkPassive().then(() => {
        const d = snapshot().deadline;
        if (d !== null && d - Date.now() <= AUTO_LOGOUT_WARN_SEC * 1000) setWarn(true);
      }), warnAt - Date.now());
      return () => window.clearTimeout(t);
    }
    setWarn(true);
    return undefined;
  }, [loggedIn, st.ended, st.deadline]);

  // 帯のあいだ：1秒ごとに残りを描き直す。期限が来たら passive で確かめる（切れていれば止めの画面）
  useEffect(() => {
    if (!warn) return;
    void flushUnsaved(); // ④ 帯を出す時点で書きかけを送る
    // 期限を過ぎたら確かめる（答えを待つあいだは重ねない・つながらないときは 10 秒ごと）
    let checking = false;
    let lastCheck = 0;
    const t = window.setInterval(() => {
      setTick((x) => x + 1);
      const d = snapshot().deadline;
      if (d !== null && Date.now() >= d && !checking && Date.now() - lastCheck > 10_000) {
        checking = true;
        lastCheck = Date.now();
        void checkPassive().finally(() => {
          checking = false;
          lastCheck = Date.now() - 9_000; // 答えが届いたら次は1秒後（境目の秒で ok が返ったとき）
        });
      }
    }, 1000);
    return () => window.clearInterval(t);
  }, [warn]);

  // 人の操作：帯があれば延ばして消す。帯が無くても、前に延ばしてから1分たっていれば延ばす（API を呼ばない入力・スクロール）
  useEffect(() => {
    if (!loggedIn) return;
    return onUserInput(() => {
      if (snapshot().ended) return;
      if (warnRef.current || Date.now() - lastActionSent() > ALIVE_EVERY_MS) void sendAlive();
    });
  }, [loggedIn]);

  // A-d：タブに戻った・スリープから戻った（30 秒ごとの時計が 60 秒以上ずれた）ら passive で確かめる
  useEffect(() => {
    if (!loggedIn) return;
    // タブを離れてから1分たっていれば（行き来のたびには読まない）
    let hiddenAt = 0;
    const onVisible = () => {
      if (document.visibilityState === "hidden") hiddenAt = Date.now();
      else if (Date.now() - hiddenAt > 60_000) void checkPassive();
    };
    document.addEventListener("visibilitychange", onVisible);
    let last = Date.now();
    const t = window.setInterval(() => {
      const now = Date.now();
      if (now - last > 60_000) void checkPassive();
      last = now;
    }, 30_000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(t);
    };
  }, [loggedIn]);

  // 切れた：控えを捨てる（情報保護）。expired はそのままログインの画面へ、idle は止めの画面のボタンで
  useEffect(() => {
    if (!st.ended) return;
    releaseLeaveGuards(); // 書きかけは退避した（④）。離れるときの確かめで止めない
    void qc.cancelQueries();
    qc.clear();
    if (st.ended.reason === "expired") window.location.replace(loginPathWithNext(st.ended.path));
  }, [st.ended, qc]);

  // 止めの画面 g が出たままのページに戻ってきた（ブラウザの「戻る」で控え＝bfcache から戻った・ほかのタブ・スリープの戻り）：
  // その間にほかのタブでログインし直していれば、この Cookie はもう有効なセッション＝止めの画面を出し続けずに読み込み直す
  // （#192、docs/76 §0.3 ナ。2026-10-16 18:48 本番：サーバーのセッションは残り 24 分なのに、18:15 に切れたページの止めの画面が戻ってきた）。
  // 確かめは passive の /me（操作の時刻は進めない）。切れたままなら 401 で、止めの画面のまま
  useEffect(() => {
    if (st.ended?.reason !== "idle") return;
    let checking = false;
    const recheck = () => {
      if (checking || document.visibilityState === "hidden") return;
      checking = true;
      void api<Me>("GET", "/me", undefined, PASSIVE)
        .then(() => window.location.reload())
        .catch(() => undefined)
        .finally(() => {
          checking = false;
        });
    };
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted) recheck();
    };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("pageshow", onShow);
    window.addEventListener("focus", recheck);
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("pageshow", onShow);
      window.removeEventListener("focus", recheck);
    };
  }, [st.ended]);

  // 止めの画面 g：画面全体に重ね、下の画面は操作できなくする
  useEffect(() => {
    const root = document.getElementById("root");
    if (!root || st.ended?.reason !== "idle") return;
    root.inert = true;
    return () => {
      root.inert = false;
    };
  }, [st.ended]);

  if (st.ended?.reason === "idle") {
    return createPortal(
      <div className="stop-over" role="alert" aria-live="assertive">
        <IdleStop path={st.ended.path} />
      </div>,
      document.body,
    );
  }
  if (!warn || !loggedIn || st.deadline === null) return null;
  const left = Math.max(0, (st.deadline - Date.now()) / 1000);
  const keep = () =>
    void api<Me>("GET", "/me", undefined, USER_ACTION)
      .then((m) => setMe(qc, m))
      .catch(() => undefined);
  return createPortal(
    <div className="autologout-band" role="region" aria-label={AUTO_LOGOUT_MSG.keep} data-testid="autologout-band" data-notice-above>
      <p className="autologout-text">
        <span aria-hidden="true" data-testid="autologout-text">{AUTO_LOGOUT_MSG.warn(countdownText(left))}</span>
        {/* 読み上げは1回だけ（毎秒の残りは読ませない） */}
        <span className="vh" role="alert">{AUTO_LOGOUT_MSG.warnSr}</span>
      </p>
      <button type="button" className="btn primary" onClick={keep} data-testid="autologout-keep">
        {AUTO_LOGOUT_MSG.keep}
      </button>
    </div>,
    document.body,
  );
}

export { checkPassive };
